import { useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

const Accordion = ({ expanded, children }) => {
  // By using `AnimatePresence` to mount and unmount the contents, we can animate
  // them in and out while also only rendering the contents of open accordions
  return (
    <>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            {children}
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};

const Group: FC<Props> = ({
  header,
  children,
  initialState = false,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(initialState);
  const toggle = (e: React.MouseEvent) => {
    setIsExpanded(!isExpanded);
    e.stopPropagation();
  };

  return (
    <div className={styles.wrapper}>
      {variant === "section" ? (
        <div className={styles.sectionTitle} onClick={toggle}>
          <span>{header}</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={isExpanded ? faChevronDown : faChevronRight}
            title={isExpanded ? t("common.collapse") : t("common.expand")}
          />
        </div>
      ) : (
        <div className={styles.header}>
          <div className={styles.title} onClick={toggle}>
            {header}
          </div>
          <div onClick={toggle} className={styles.expanderWrapper}>
            <FontAwesomeIcon
              className={classNames(styles.expander)}
              icon={isExpanded ? faCaretDown : faCaretUp}
              title={isExpanded ? t("common.collapse") : t("common.expand")}
            />
          </div>
        </div>
      )}
      <Accordion expanded={isExpanded} setExpanded={setIsExpanded}>
        {children}
      </Accordion>
    </div>
  );
};

export default Group;
